import React, { useEffect, useRef, useState } from 'react'
import { Input, Button, Dropdown, Radio } from '@/components/atoms/index'
import WordTableList from '@/components/organisms/Table/AcademyWordTable'
import { Icon } from '@/icons'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getSlugPrefix } from '@/utils/slug'
import { academyWordApi } from '@/services/api'
import { useSessionSlug } from '@/hooks/useSessionSlug'
import * as XLSX from 'xlsx'
import { message, Modal } from 'antd'

export default function AcademyWordDetail() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const slugPrefix = getSlugPrefix(pathname)
  const { wordId } = useParams()

  const [registrationType, setRegistrationType] = useState<'excel' | 'individual'>('excel')
  const [word, setWord] = useState('')
  const [meaning, setMeaning] = useState('')
  const [gender, setGender] = useState('female')
  const [age, setAge] = useState('adult')
  const [speed, setSpeed] = useState('normal')
  const [pitch, setPitch] = useState('medium')
  const [submitting, setSubmitting] = useState(false)
  const [wordUsageCount, setWordUsageCount] = useState(0)
  const [initialWordMeta, setInitialWordMeta] = useState({ word: '', gender: 'female', age: 'adult', speed: 'normal', pitch: 'medium' })
  const { academyCode: sessionAcademyCode } = useSessionSlug()
  const academyCode = sessionAcademyCode || ''
  // 엑셀 업로드 상태
  type UploadRow = {
    id: string
    wordCode: string
    word: string
    meaning: string
    ttsType: string
    registrant: string
    createdAt: string
    uploadStatus: 'completed' | 'failed'
    gender: string
    age: string
    speed: string
    pitch: string
    audioUrl?: string
    selected?: boolean
  }
  const [uploadRows, setUploadRows] = useState<UploadRow[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleDownloadTemplate = () => {
    // 1행: 단어, 해석
    const header = [
      ['단어', '해석']
    ]
    const ws = XLSX.utils.aoa_to_sheet(header)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'template')
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'word_template.xlsx'
    a.click()
    URL.revokeObjectURL(url)
  }

  const checkRowsExistence = async (rows: UploadRow[]) => {
    const results = await Promise.all(rows.map(async (row) => {
      try {
        const res = await academyWordApi.exists({ word: row.word, gender: row.gender, age: row.age, speed: row.speed, pitch: row.pitch })
        const data = res?.data || res
        const ok = !data?.exists
        const status: UploadRow['uploadStatus'] = ok ? 'completed' : 'failed'
        return { id: row.id, status }
      } catch {
        return { id: row.id, status: 'failed' as UploadRow['uploadStatus'] }
      }
    }))
    const statusById = new Map(results.map(r => [r.id, r.status]))
    return rows.map(r => ({ ...r, uploadStatus: statusById.get(r.id) || r.uploadStatus }))
  }

  const handleExcelFile = async (file: File) => {
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf, { type: 'array' })
    const ws = wb.Sheets[wb.SheetNames[0]]
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[]
    // 1행 헤더 무시
    const body = rows.slice(1)
    const mapped: UploadRow[] = (body || []).map((r: any[], idx: number) => {
      const w = (r?.[0] || '').toString().trim()
      const m = (r?.[1] || '').toString().trim()
      const status: UploadRow['uploadStatus'] = (w && m) ? 'completed' : 'failed'
      return {
        id: `${Date.now()}_${idx}`,
        wordCode: '',
        word: w,
        meaning: m,
        ttsType: `${gender}/${age}/${speed}/${pitch}`,
        registrant: '',
        createdAt: '',
        uploadStatus: status,
        gender, age, speed, pitch,
        selected: false,
      }
    })
    const checked = await checkRowsExistence(mapped)
    setUploadRows(checked)
    setSelectedId(checked.length ? checked[0].id : null)
  }

  // TTS 유형 변경 시 즉시 상태 재검증 및 표시
  useEffect(() => {
    if (uploadRows.length === 0) return
    const retype = uploadRows.map(r => ({ ...r, ttsType: `${gender}/${age}/${speed}/${pitch}`, gender, age, speed, pitch }))
    checkRowsExistence(retype).then(setUploadRows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, age, speed, pitch])

  const handleDeleteRow = (id: string) => {
    setUploadRows(prev => prev.filter(r => r.id !== id))
  }

  const handleRowSelect = (id: string) => {
    setSelectedId(prev => (prev === id ? null : id))
  }

  const handleTtsPreview = async () => {
    try {
      let targetWord = word
      let g = gender, a = age, s = speed, p = pitch
      if (registrationType === 'excel') {
        if (!selectedId) return
        const row = uploadRows.find(r => r.id === selectedId)
        if (!row) return
        targetWord = row.word
        g = row.gender; a = row.age; s = row.speed; p = row.pitch
      }
      const blob = await academyWordApi.preview({ word: targetWord, gender: g, age: a, speed: s, pitch: p })
      const url = URL.createObjectURL(new Blob([blob], { type: 'audio/mpeg' }))
      const audio = new Audio(url)
      audio.play()
    } catch (e) {
      console.error(e)
    }
  }

  const handleBulkCreate = async () => {
    const targets = uploadRows.filter(r => r.uploadStatus === 'completed')
    if (targets.length === 0) {
      message.warning('등록 가능한 단어가 없습니다.')
      return
    }
    setSubmitting(true)
    try {
      if (!academyCode) {
        alert('학원 정보를 불러오지 못했습니다.')
        return
      }
      const registrantName = localStorage.getItem('academy_user_name') || ''
      const registrantUserId = Number(localStorage.getItem('academy_user_id') || 0) || undefined
      let successCount = 0
      let duplicateCount = 0
      let failCount = 0
      for (const t of targets) {
        try {
          const response = await academyWordApi.create({ word: t.word, meaning: t.meaning, gender: t.gender, age: t.age, speed: t.speed, pitch: t.pitch, registrant: registrantName, registrantUserId, academyCode })
          if (response?.success === false) {
            const respMsg = response?.message || ''
            if (respMsg.includes('이미 등록된')) {
              duplicateCount++
            } else {
              failCount++
            }
          } else {
            successCount++
          }
        } catch (error: any) {
          const errorMsg = error?.response?.data?.message || error?.message || ''
          if (errorMsg.includes('이미 등록된')) {
            duplicateCount++
          } else {
            failCount++
          }
        }
      }
      if (successCount > 0 && duplicateCount === 0 && failCount === 0) {
        message.success('단어 등록 및 음원 생성이 완료되었습니다.') // [QA Fix] 일괄 등록 성공 메시지
      } else if (duplicateCount > 0 && failCount === 0) {
        message.info(`이미 등록된 단어 ${duplicateCount}개 외 나머지 단어가 등록되었습니다.`)
      } else {
        message.info(`등록불가한 단어 ${duplicateCount + failCount}개 외 등록하였습니다.`)
      }
      navigate(`${slugPrefix}/academy/word/list`)
    } catch (e) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }
  

  // 상세 조회 (수정 화면)
  useEffect(() => {
    const fetchDetail = async () => {
      if (!wordId) return;
      try {
        const res = await academyWordApi.detail(wordId)
        const data = res?.data || res
        setWord(data.word || '')
        setMeaning(data.meaning || '')
        setGender(data.gender || 'female')
        setAge(data.age || 'adult')
        setSpeed(data.speed || 'normal')
        setPitch(data.pitch || 'medium')
        setWordUsageCount(Number(data.usageCount || 0))
        setInitialWordMeta({
          word: data.word || '',
          gender: data.gender || 'female',
          age: data.age || 'adult',
          speed: data.speed || 'normal',
          pitch: data.pitch || 'medium',
        })
        setRegistrationType('individual')
      } catch (e) {
        console.error(e)
      }
    }
    fetchDetail()
  }, [wordId])

  const hasWordKeyChanged = () => {
    return (
      word !== initialWordMeta.word ||
      gender !== initialWordMeta.gender ||
      age !== initialWordMeta.age ||
      speed !== initialWordMeta.speed ||
      pitch !== initialWordMeta.pitch
    )
  }

  const confirmWordUpdate = () => {
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '학습이력이 있는 단어입니다.',
        content: '학습이력이 있는 단어입니다. 수정하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
  }

  const handleSubmit = async () => {
    if (!word || !meaning) return;
    setSubmitting(true)
    try {
      if (!academyCode) {
        alert('학원 정보를 불러오지 못했습니다.')
        return
      }
      if (wordId && wordUsageCount > 0) {
        const confirmed = await confirmWordUpdate() // [QA Fix] 학습이력 경고
        if (!confirmed) {
          setSubmitting(false)
          return
        }
      }
      if (!wordId) {
        const existsResponse = await academyWordApi.exists({ word, gender, age, speed, pitch })
        const existsData = existsResponse?.data || existsResponse
        if (existsData?.exists) {
          message.error('이미 등록된 단어입니다.') // [QA Fix] 개별 등록 중복 차단
          setSubmitting(false)
          return
        }
      } else if (hasWordKeyChanged()) {
        const existsResponse = await academyWordApi.exists({ word, gender, age, speed, pitch })
        const existsData = existsResponse?.data || existsResponse
        if (existsData?.exists) {
          message.error('이미 등록된 단어입니다.')
          setSubmitting(false)
          return
        }
      }
      const registrantName = localStorage.getItem('academy_user_name') || ''
      const registrantUserId = Number(localStorage.getItem('academy_user_id') || 0) || undefined
      const payload = { word, meaning, gender, age, speed, pitch, registrant: registrantName, registrantUserId, academyCode }
      if (wordId) {
        await academyWordApi.update(wordId, payload)
        message.success('단어 등록 및 음원 생성이 완료되었습니다.') // [QA Fix] 수정 완료 메시지 통일
      } else {
        await academyWordApi.create(payload)
        message.success('단어 등록 및 음원 생성이 완료되었습니다.')
      }
      navigate(`${slugPrefix}/academy/word/list`)
    } catch (e: any) {
      console.error(e)
      const errorMsg = e?.response?.data?.message || '단어 등록에 실패했습니다.'
      message.error(errorMsg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className='flex flex-col flex-1'>
    <div className='flex flex-row justify-between items-center pt-8'>
        <h1 className='pb-5 heading1'>{wordId ? '단어 수정' : '단어 등록'}</h1>
    </div>
        <div className="w-full">
            <ul>
                {!wordId && (
                  <li className='flex py-5 border-b border-gray-200'>
                      <p className='flex-none max-w-[150px] w-full text-gray-500'>등록 방식</p>
                      <div className='flex flex-row gap-5'>
                          <Radio 
                            name='word-upload' 
                            checked={registrationType === 'excel'} 
                            label='Excel 일괄 업로드' 
                            onChange={() => setRegistrationType('excel')} 
                          />
                          <Radio 
                            name='word-upload' 
                            checked={registrationType === 'individual'} 
                            label='개별 단어 등록' 
                            onChange={() => setRegistrationType('individual')} 
                          />
                      </div>
                  </li>
                )}

                {/* 엑셀 일괄 업로드 영역 */}
                {!wordId && registrationType === 'excel' && (
                  <>
                    <li className='flex py-5 border-b border-gray-200'>
                      <p className='flex-none max-w-[150px] w-full text-gray-500'>엑셀 파일</p>
                      <div className='flex flex-col gap-4 w-full'>
                        <div className='flex gap-2 w-full'>
                          <Button intent='secondary' className='w-[200px]' onClick={handleDownloadTemplate}>엑셀 양식 다운로드</Button>
                          <Button className='w-[200px]' onClick={() => fileInputRef.current?.click()}>파일 선택</Button>
                          <input ref={fileInputRef} type='file' accept='.xlsx,.xls' style={{ display: 'none' }} onChange={(e:any)=>{ const f = e?.target?.files?.[0]; if (f) handleExcelFile(f) }} />
                        </div>
                        <div className='text-sm text-gray-500'>
                          • 엑셀 파일을 업로드하여 여러 단어를 한 번에 등록할 수 있습니다.<br/>
                          • 파일 형식: .xlsx, .xls (최대 10MB)
                        </div>
                        <p className='text-sm text-gray-500'>업로드 단어 목록</p>
                        <div className='w-full max-h-[260px] overflow-y-auto'>
                          <WordTableList 
                            data={uploadRows}
                            type='display'
                            onRowClick={(id: string)=>handleRowSelect(id)}
                            onDelete={(id:string)=>handleDeleteRow(id)}
                            selectedIds={selectedId ? [selectedId] : []}
                          />
                        </div>
                      </div>
                    </li>
                  </>
                )}

                {/* 개별 단어 등록 영역 */}
                {registrationType === 'individual' && (
                  <>
                    <li className='flex py-5 border-b border-gray-200'>
                            <p className='flex-none max-w-[150px] w-full text-gray-500'>단어</p>
                            <div className='flex gap-5 w-full max-w-[800px]'>
                            <div className='w-full'>
                                <Input label='단어' placeholder='단어를 입력해주세요' className='max-w-[500px] w-full' value={word} onChange={(e: any) => setWord(e?.target?.value || '')}/>
                            </div>
                            <div className='w-full'>
                                <Input label='해석' placeholder='해석을 입력해주세요' className='max-w-[500px] w-full' value={meaning} onChange={(e: any) => setMeaning(e?.target?.value || '')}/>
                            </div>
                            </div>
                    </li>

                  </>
                )}

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>TTS 유형</p>
                    <div className='grid grid-cols-2 gap-y-4 gap-x-6 w-full max-w-[800px]'>
                        <div className='flex flex-col gap-2'>
                            <p className='text-sm text-gray-500'>음성 성별</p>
                            <Dropdown placeholder='음성 성별을 선택해주세요' className='w-full' value={gender} onChange={(v)=>setGender(v)} options={[{ value: 'female', label: '여성' }, { value: 'male', label: '남성' }]} />
                        </div>
                        <div className='flex flex-col gap-2'>
                            <p className='text-sm text-gray-500'>연령대</p>
                            <Dropdown placeholder='연령대를 선택해주세요' className='w-full' value={age} onChange={(v)=>setAge(v)} options={[{ value: 'child', label: '아동' }, { value: 'young', label: '청소년' }, { value: 'adult', label: '성인' }, { value: 'elder', label: '노년' }]} />
                        </div>
                        <div className='flex flex-col gap-2'>
                            <p className='text-sm text-gray-500'>속도</p>
                            <Dropdown placeholder='속도를 선택해주세요' className='w-full' value={speed} onChange={(v)=>setSpeed(v)} options={[{ value: 'slow', label: '느림' }, { value: 'normal', label: '보통' }, { value: 'fast', label: '빠름' }]} />
                        </div>
                        <div className='flex flex-col gap-2'>
                            <p className='text-sm text-gray-500'>피치</p>
                            <Dropdown placeholder='피치를 선택해주세요' className='w-full' value={pitch} onChange={(v)=>setPitch(v)} options={[{ value: 'low', label: '낮음' }, { value: 'medium', label: '보통' }, { value: 'high', label: '높음' }]} />
                        </div>
                        <Button outline className='w-full' onClick={handleTtsPreview}>
                            <Icon name='listen' className='*:fill-indigo-500' size={24} />
                            미리 듣기</Button>
                    </div>
                </li>

                

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>등록자</p>
                    <p className=''>{localStorage.getItem('academy_user_name') || '-'}</p>
                </li>
            </ul>
            <div className='flex justify-end *:w-[120px] gap-2  my-5'>
                <Button intent='secondary' onClick={()=>navigate(-1)}>뒤로가기</Button>
                {registrationType === 'excel' ? (
                  <Button onClick={handleBulkCreate} disabled={submitting}>등록</Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={submitting}>{wordId ? '수정' : '등록'}</Button>
                )}
            </div>
        </div>
    </main>
  )
}